import { createNewDate, createNewUtcDate, userTimeZone } from '@mixins/moment-mixin';
import StringMixin from '@mixins/string-mixin';
import { ITEM_TYPE_TANK } from '@models/item';
import PurchaseRequest from '@models/purchase-request';
import {
    ROUTE_STATUS_COMPLETED, ROUTE_STATUS_IN_PROGRESS, ROUTE_TYPE_DELIVERY, ROUTE_TYPE_SERVICE,
} from '@models/route';
import RouteApp from '@models/route-app';
import RouteTask, { ROUTE_TASK_STATUS_ARRIVED, ROUTE_TASK_STATUS_COMPLETED } from '@models/route-task';
import * as Sentry from '@sentry/vue';
import { debounce } from 'lodash';
import sortBy from 'lodash/sortBy';

import ApiClient from '@/api-client';

const defaultState = {
    route: {
        tasks: [],
    },
    groupedTasks: [],
    tasksToBeGrouped: [],
    relationships: [],
    inventory: {},
    optimizing: false,
    showCompletedOrders: false,
    editTask: {},
    networkConnectionError: false,
};

export default {
    namespaced: true,
    state: defaultState,
    getters: {
        completedTasks(state) {
            return state.route.tasks.filter((t) => t.status === ROUTE_TASK_STATUS_COMPLETED && t.order_id);
        },
        routeTasks(state) {
            const completedTasks = sortBy(state.route.tasks.filter((task) => task.status === ROUTE_TASK_STATUS_COMPLETED), 'completed_at');
            const arrivedTasks = sortBy(state.route.tasks.filter((task) => task.status === ROUTE_TASK_STATUS_ARRIVED), 'arrived_at');
            const remainingTasks = sortBy(state.route.tasks.filter((task) => task.status === null), 'estimated_start_time');

            return [...completedTasks, ...arrivedTasks, ...remainingTasks];
        },
        // has arrived task with prevailing wage customer location
        hasArrivedTaskWithPrevailingWage(state) {
            return state.route.tasks.filter((task) => task.status === ROUTE_TASK_STATUS_ARRIVED
                && task.order.customer_location?.prevailing_wage).length > 0;
        },
        routeStarted: (state) => (state.route.tasks ? !!state.route.tasks.filter((routeTask) => routeTask.status === ROUTE_TASK_STATUS_COMPLETED).length : false),
        routeFinished: (state) => state.route.status === ROUTE_STATUS_COMPLETED,
        allTasksCompleted: (state) => {
            if (state.route.status) {
                return state.route.tasks
                    .filter((task) => task.order_id)
                    .filter((task) => task.status !== ROUTE_TASK_STATUS_COMPLETED)
                    .length === 0;
            }

            return false;
        },
        isCoDriver: (state, getters, rootState) => state.route.co_driver_user_id === rootState.AuthenticationStore.user.id,
        // eslint-disable-next-line max-len
        getNextAvailableOrder: (state) => (state.route.tasks && state.route.tasks.filter((task) => task.status !== ROUTE_TASK_STATUS_COMPLETED && task.order_id).length ? state.route.tasks.filter((task) => task.status !== ROUTE_TASK_STATUS_COMPLETED && task.order_id)[0] : false),
        isDeliveryRoute: (state) => state.route.type === ROUTE_TYPE_DELIVERY,
        isServiceRoute: (state) => state.route.type === ROUTE_TYPE_SERVICE,
    },
    mutations: {
        resetState(state) {
            Object.assign(state, defaultState);
        },
        setInventory(state, inventory) {
            state.inventory = inventory;
        },
        route(state, route) {
            state.route = route;
            state.route.user_time_zone = userTimeZone;
        },
        updateRouteTaskOrder(state, { data, task }) {
            const index = state.route.tasks.findIndex((t) => t.id === task.id);

            if (index !== -1) {
                const routeTask = state.route.tasks[index];
                routeTask.order = { ...routeTask.order, ...data };

                const updatedRouteTask = { ...routeTask };
                state.route.tasks.splice(index, 1, updatedRouteTask);
            }
        },
        updateCustomerLocation(state, { task, data }) {
            const routeTaskIndex = state.route.tasks.findIndex((t) => task.id === t.id);

            if (routeTaskIndex !== -1) {
                const routeTask = state.route.tasks[routeTaskIndex];
                routeTask.order.customer_location = { ...routeTask.order.customer_location, ...data };
                state.route.tasks.splice(routeTaskIndex, 1, routeTask);
            }

            const groupedTaskIndex = state.groupedTasks.findIndex((t) => task.id === t.id);
            if (groupedTaskIndex !== -1) {
                const groupedTask = state.groupedTasks[groupedTaskIndex];
                groupedTask.order.customer_location = { ...groupedTask.order.customer_location, ...data };
                state.groupedTasks.splice(groupedTaskIndex, 1, groupedTask);
            }
        },
        updateGroupedRouteTaskOrderDeliveryItem(state, { item, data }) {
            const task = state.groupedTasks.find((t) => t.order_id === item.order_id);

            const index = task.order.items.findIndex((oi) => oi.id === item.id);

            task.order.items.splice(index, 1, { ...task.order.items[index], ...data });

            const inventoryItem = state.inventory[item.order_id]?.[item.id];
            if (inventoryItem) {
                inventoryItem.qty = data.total_on_site;
            }
        },
        updateRouteTaskOrderDeliveryItem(state, { item, data }) {
            const task = state.route.tasks.find((t) => t.order_id === item.order_id);

            const index = task.order.items.findIndex((oi) => oi.id === item.id);

            task.order.items.splice(index, 1, { ...task.order.items[index], ...data });

            const inventoryItem = state.inventory[item.order_id]?.[item.id];
            if (inventoryItem) {
                inventoryItem.qty = data.total_on_site;
            }
        },
        updateGroupedRouteTaskOrderServiceItem(state, { item, data }) {
            const task = state.groupedTasks.find((t) => t.order_id === item.order_id);

            if (!task?.order) {
                return;
            }

            const index = task.order.items.findIndex((s) => s.id === item.id);

            if (index !== -1) {
                task.order.items.splice(index, 1, { ...task.order.items[index], ...data });
            }
        },
        updateRouteTaskOrderServiceItem(state, { item, data }) {
            const task = state.route.tasks.find((t) => t.order_id === item.order_id);

            if (!task?.order) {
                return;
            }

            const index = task.order.items.findIndex((s) => s.id === item.id);

            if (index !== -1) {
                task.order.items.splice(index, 1, { ...task.order.items[index], ...data });
            }
        },
        groupTask(state, task) {
            if (typeof task === 'object') {
                state.groupedTasks.push(new RouteTask({ ...task }));
            } else {
                Sentry.setContext('task', task);
                Sentry.captureMessage('Group task error');
            }
        },
        addTaskToBeGrouped(state, task) {
            if (typeof task === 'object') {
                state.tasksToBeGrouped.push(new RouteTask({ ...task }));
            } else {
                Sentry.setContext('task', task);
                Sentry.captureMessage('Add task to be grouped error');
            }
        },
        removeTaskToBeGrouped(state, task) {
            const index = state.tasksToBeGrouped.findIndex((groupedRouteTask) => groupedRouteTask.id === task.id);

            if (index !== -1) {
                state.tasksToBeGrouped.splice(index, 1);
            }
        },
        removeGroupTask(state, task) {
            const index = state.groupedTasks.findIndex((groupedRouteTask) => groupedRouteTask.id === task.id);

            if (index !== -1) {
                state.groupedTasks.splice(index, 1);
            }
        },
        emptyGroup(state) {
            state.groupedTasks = [];
        },
        emptyTasksToBeGrouped(state) {
            state.tasksToBeGrouped = [];
        },
        markRouteAsCompleted(state) {
            const endBranchIndex = state.route.tasks.findIndex((t) => t.branch_id && t.status === null && !t.starting_loation);

            if (endBranchIndex !== -1) {
                const routeTask = {
                    ...state.route.tasks[endBranchIndex],
                    status: ROUTE_TASK_STATUS_COMPLETED,
                    completed_at: createNewUtcDate().toISOString(),
                    arrived_at: createNewUtcDate().toISOString(),
                };

                state.route.tasks.splice(endBranchIndex, 1, routeTask);

                state.route.status = ROUTE_STATUS_COMPLETED;
                state.route.finish_time = createNewUtcDate().toISOString();
            }
        },
        optimizing(state, value) {
            state.optimizing = value;
        },
        startRoute(state) {
            const index = state.route.tasks.findIndex((t) => t.starting_location && t.branch_id);

            if (index !== -1 && !state.route.tasks[index].status) {
                const now = createNewUtcDate();
                state.route.tasks.splice(index, 1, {
                    ...state.route.tasks[index],
                    completed_at: now.toISOString(),
                    arrived_at: now.toISOString(),
                    status: ROUTE_TASK_STATUS_COMPLETED,
                });

                state.route.status = ROUTE_STATUS_IN_PROGRESS;
            }
        },
        updateRouteTask(state, { task, data }) {
            const index = state.route.tasks.findIndex((t) => t.id === task.id);

            if (index !== -1) {
                const routeTask = { ...state.route.tasks[index], ...data, user_time_zone: userTimeZone };
                state.route.tasks.splice(index, 1, routeTask);
            }
        },
        updateGroupedTask(state, { task, data }) {
            const index = state.groupedTasks.findIndex((t) => t.id === task.id);

            if (index !== -1) {
                const routeTask = { ...state.groupedTasks[index], ...data, user_time_zone: userTimeZone };

                state.groupedTasks.splice(index, 1, routeTask);
            }
        },
        showCompletedOrders(state, value) {
            state.showCompletedOrders = value;
        },
        editTask(state, task) {
            state.editTask = task;
        },
        updateStartTime(state, startTime) {
            state.route.estimated_start_time = startTime;
        },
        setNetworkConnectionError(state, boolean) {
            state.networkConnectionError = boolean;
        },
        addOrderServiceItem(state, { item, order }) {
            const task = state.groupedTasks.find((t) => t.order_id === order.id);

            task.order.items.push(item);
        },
        addTank(state, { data, order }) {
            const task = state.groupedTasks.find((t) => t.order_id === order.id);

            const item = {
                id: Date.now(),
                new: true,
                created_at: createNewDate().toISOString(),
                order_id: order.id,
                pickup_qty: data.delivered_qty,
                item_type_id: ITEM_TYPE_TANK,
                qty: 0,
                ...data,
            };

            task.order.items.push(item);
        },
        addTankRequest(state, { data, order }) {
            const task = state.groupedTasks.find((t) => t.order_id === order.id);

            const item = {
                id: Date.now(),
                new: true,
                created_at: createNewDate().toISOString(),
                order_id: order.id,
                ...data,
                qty: data.delivered_qty,
            };

            task.order.tank_requests.push(item);
        },
        updateTankRequest(state, { data, order, tankRequestId }) {
            let task = state.groupedTasks
                .find((t) => t.order_id === order.id);

            if (!task) {
                task = state.route.tasks
                    .find((t) => t.order_id === order.id);
            }

            const tankRequestIndex = task.order.tank_requests.findIndex((t) => t.id === tankRequestId);
            if (tankRequestIndex !== -1) {
                task.order.tank_requests.splice(tankRequestIndex, 1, { ...task.order.tank_requests[tankRequestIndex], ...data });
            }
        },
        addPurchaseRequestToOrder(state, { purchaseRequest, order }) {
            const task = state.groupedTasks
                .find((t) => t.order_id === order.id);

            if (task) {
                const purchaseRequestIndex = task.order.purchase_requests.findIndex((p) => p.id === purchaseRequest.id);

                if (purchaseRequestIndex === -1) {
                    task.order.purchase_requests.push({ ...purchaseRequest });
                } else {
                    task.order.purchase_requests.splice(purchaseRequestIndex, 1, { ...purchaseRequest });
                }
            }
        },
        removePurchaseRequestFromOrder(state, { purchaseRequest, order }) {
            const task = state.groupedTasks.find((t) => t.order_id === order.id);

            if (task) {
                // eslint-disable-next-line camelcase
                const { purchase_requests } = task.order;
                const purchaseRequestIndex = purchase_requests.findIndex((p) => p.id === purchaseRequest.id);

                if (purchaseRequestIndex !== -1) {
                    purchase_requests.splice(purchaseRequestIndex, 1);
                }
            }
        },
        addWaterSystemLogToOrder(state, { routeTask, waterSystemLog }) {
            const task = state.groupedTasks
                .find((t) => t.id === routeTask.id);

            task.order.water_system_logs.push(waterSystemLog);
        },
        addNewServiceItemToOrder(state, item) {
            const task = state.groupedTasks
                .find((t) => t.order_id === item.order_id);

            if (task) {
                task.order.items.push(item);
            }
        },
        RESTORE_ROUTE(state, data) {
            state.route = data.route ? new RouteApp(data.route) : { tasks: [] };
            state.groupedTasks = data.groupedTasks ? data.groupedTasks.map((t) => new RouteTask(t)) : [];
            state.tasksToBeGrouped = data.tasksToBeGrouped ? data.tasksToBeGrouped.map((t) => new RouteTask(t)) : [];
            state.relationships = data.relationships || [];
            state.inventory = data.inventory || {};
            state.optimizing = data.optimizing || false;
            state.showCompletedOrders = data.showCompletedOrders || false;
            state.editTask = data.editTask ? new RouteTask(data.editTask) : {};
            state.networkConnectionError = data.networkConnectionError || false;
        },
    },
    actions: {
        removePurchaseRequestFromOrder({ commit }, { purchaseRequest, order }) {
            commit('removePurchaseRequestFromOrder', { purchaseRequest, order });
            ApiClient.delete(`/api/purchase-requests/${purchaseRequest.id}`);
        },
        async getRouteById({ state, commit, dispatch }, id) {
            if (state.networkConnectionError) {
                await dispatch('updateRoute', false);
            } else {
                const route = await RouteApp.find(id);

                commit('route', route);
                commit('setNetworkConnectionError', false);
            }
        },
        async getRouteInventoryById({ state, commit, dispatch }, id) {
            if (state.networkConnectionError) {
                await dispatch('updateRoute', false);
            } else {
                const response = await ApiClient.get(`/api/route-app/${id}/inventory`);
                commit('setInventory', response.data.data);
                commit('setNetworkConnectionError', false);
            }
        },
        async sendNotification({ state }) {
            await ApiClient.patch(`/api/route-app/${state.route.id}/notify`, { grouped_tasks: state.tasksToBeGrouped });
        },
        async updateRoute({ dispatch, state, commit }, optimize = true) {
            const newRoute = new RouteApp({ ...state.route, grouped_orders: state.groupedTasks });

            try {
                if (optimize) {
                    await dispatch('toggleOptimizationFlag', true);
                }

                const route = await state.route.save();
                commit('route', route);

                try {
                    await ApiClient.patch(`/api/route-app/${state.route.id}/inventory`, { orders: state.inventory });
                } catch (e) {
                    // fail silently
                }

                if (optimize) {
                    dispatch('optimizeRoute');
                }

                commit('setNetworkConnectionError', false);
            } catch (e) {
                Sentry.setContext('route', newRoute);
                Sentry.captureException(e);
                commit('route', newRoute);
                if (optimize) {
                    dispatch('toggleOptimizationFlag', false);
                }

                commit('setNetworkConnectionError', true);
            }
        },
        optimizeRoute: debounce(async ({ state, dispatch, commit }) => {
            try {
                dispatch('toggleOptimizationFlag', true);
                await ApiClient.post(`/api/route-app/${state.route.id}/optimize`, { grouped_tasks: state.groupedTasks });
                commit('setNetworkConnectionError', false);
            } catch (e) {
                dispatch('toggleOptimizationFlag', false);
                commit('setNetworkConnectionError', true);
            }
        }, 500),
        async startRoute({ commit, dispatch, state }) {
            await state.route.refresh();
            commit('startRoute');
            dispatch('updateRoute');
        },
        async endRoute({ commit, dispatch, state }) {
            commit('markRouteAsCompleted');
            await dispatch('updateRoute', false);

            try {
                localStorage.removeItem(`route-${state.route.id}`);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
            }
        },
        async completeOrders({ commit, state, dispatch }, fields) {
            state.groupedTasks.forEach((groupedTask) => {
                commit('updateRouteTaskOrder', {
                    task: groupedTask,
                    data: {
                        ...groupedTask.order,
                        signature: fields.signature || null,
                        signature_name: fields.signature_name || null,
                    },
                });
                const { order, ...routeTask } = groupedTask;
                commit('updateRouteTask', {
                    task: groupedTask,
                    data: {
                        ...routeTask,
                        status: ROUTE_TASK_STATUS_COMPLETED,
                        completed_at: createNewUtcDate().toISOString(),
                    },
                });
            });

            dispatch('updateRoute');
        },
        async updateCustomerLocation({ commit, dispatch }, { routeTask, data }) {
            commit('updateCustomerLocation', { task: routeTask, data });
            await dispatch('updateRoute', false);
        },
        async markRouteTasksAsArrived({
            dispatch, commit, getters, state,
        }) {
            commit('emptyGroup');
            // remove any tasks which are set to arrived
            getters.routeTasks
                .filter((task) => task.status === ROUTE_TASK_STATUS_ARRIVED)
                .forEach((task, routeTaskIndex) => {
                    commit('updateRouteTask', {
                        task: routeTaskIndex,
                        data: {
                            status: null,
                            arrived_at: null,
                        },
                    });
                });

            state.tasksToBeGrouped.forEach((groupedTask) => {
                const updateData = {
                    status: ROUTE_TASK_STATUS_ARRIVED,
                    arrived_at: createNewUtcDate().toISOString(),
                };

                const index = state.route.tasks.findIndex((task) => task.id === groupedTask.id);
                commit('groupTask', new RouteTask({ ...state.route.tasks[index] }));
                commit('updateRouteTask', {
                    task: groupedTask,
                    data: updateData,
                });
                commit('updateGroupedTask', {
                    task: groupedTask,
                    data: updateData,
                });
            });

            await dispatch('updateRoute');

            commit('emptyTasksToBeGrouped');
        },
        returnVisitRequiredForRouteTasks({ commit, state }, boolean) {
            state.groupedTasks.forEach((groupedTask) => {
                commit('updateGroupedTask', {
                    task: groupedTask,
                    data: {
                        reschedule_required: boolean,
                    },
                });
            });
        },
        cancelArrival({ commit, dispatch, state }) {
            state.groupedTasks.forEach((groupedTask) => {
                commit('updateRouteTask', {
                    task: groupedTask,
                    data: {
                        status: null,
                        arrived_at: null,
                    },
                });
                commit('updateGroupedTask', {
                    task: groupedTask,
                    data: {
                        status: null,
                        arrived_at: null,
                    },
                });
            });

            commit('emptyGroup');
            dispatch('updateRoute');
        },
        updateGroupedRouteTasks({ commit, state }, data) {
            state.groupedTasks.forEach((groupedTask) => {
                commit('updateRouteTask', {
                    task: groupedTask,
                    data,
                });
            });
        },
        addToGroup({ commit, state }, routeTask) {
            const routeTaskIsGrouped = state.groupedTasks.find((gt) => gt.id === routeTask.id);
            if (!routeTaskIsGrouped) {
                commit('groupTask', routeTask);
            }
        },
        removeFromGroup({ commit }, routeTask) {
            commit('removeGroupTask', routeTask);
        },
        emptyGroup({ commit }) {
            commit('emptyGroup');
        },
        toggleOptimizationFlag({ commit, dispatch }, state) {
            commit('optimizing', state);
            dispatch('LoadingStore/isLoading', state, { root: true });
        },
        showCompletedOrders({ commit, state }) {
            commit('showCompletedOrders', !state.showCompletedOrders);
        },
        editTask({ commit }, task) {
            commit('editTask', task);
        },
        resetState({ commit }) {
            commit('resetState');
            commit('emptyGroup');
        },
        updateStartTime({ commit, dispatch }, startTime) {
            commit('updateStartTime', createNewDate(startTime, 'h:mm a').toISOString());
            dispatch('updateRoute');
        },
        updateRouteTask({ commit }, { routeTask, updateValues }) {
            commit('updateRouteTask', {
                task: routeTask,
                data: updateValues,
            });
        },
        async updatePurchaseRequestsForAllOrders({ commit }, { order, purchaseRequestId }) {
            const purchaseRequest = await PurchaseRequest
                .with('purchaseOrders', 'items', 'items.item', 'purchaseOrders.items', 'purchaseOrders.items.itemReceipts')
                .find(purchaseRequestId);

            commit('addPurchaseRequestToOrder', { purchaseRequest, order });
        },
        async getServiceItemsForOrder({ state, commit }, itemIds) {
            const items = await ApiClient.getCollection(`/api/route-app/${state.route.id}/items`, null, {
                'filter[items]': itemIds,
            });

            items.forEach((i) => {
                commit('addNewServiceItemToOrder', i);
            });
        },
        async loadData({ commit, state, dispatch }, routeId) {
            const storedData = localStorage.getItem(`route-${routeId}`);
            const jsonData = storedData ? JSON.parse(StringMixin.methods.decompress(storedData)) : null;

            if (jsonData) {
                commit('RESTORE_ROUTE', jsonData);
            }

            if (state.networkConnectionError) {
                await dispatch('updateRoute', false);
            }
        },
    },
};
